import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { SuiClientProvider, WalletProvider } from '@mysten/dapp-kit';
import { getFullnodeUrl } from '@mysten/sui/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import EnokiWalletRegistration from './services/EnokiWalletRegistration';
import Layout from './components/Layout';
import ProductList from './pages/ProductList';
import Post from './pages/Post';
import CreatePost from './pages/CreatePost';
import Account from './pages/Account';
import Login from './pages/Login';
import ProtectedRoute from './utils/ProtectedRoute';
import AuthProvider from "./store/AuthProvider";
import MarkdownTest from './components/Post/MarkdownTest';
import MarkdownTestInput from './components/Post/MarkdownTestInput';
import MarkdownUpdate from './components/Post/MarkdownUpdate';
import ChatGPTSaverPrivacy from './pages/ChatGPTSaver/Privacy';
import AIMeetingPro from './pages/AIMeetingPro';
import AIMeetingProDownload from './pages/AIMeetingPro/Download/AIMeetingProDownload';
import AIMeetingProPost from './pages/AIMeetingPro/AIMeetingProPost';
import NoMatch from './pages/NoMatch';
import NoMatchChatRoom from './pages/NoMatch/ChatRoom';
import NoMatchSettings from './pages/NoMatch/Settings';
import ZkGoogleCallback from './pages/zkGoogleCallback';
import Game2048Eth from './pages/Game2048/ETH/2048Eth';
import Game2048Sui from './pages/Game2048/Sui/2048Sui';

// 載入多語言支援
import './i18n';
import Future250705 from './pages/Future250705';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// 創建 QueryClient
const queryClient = new QueryClient();

// Sui 網路配置
const networks = {
  mainnet: { url: getFullnodeUrl('mainnet') },
  testnet: { url: getFullnodeUrl('testnet') },
  devnet: { url: getFullnodeUrl('devnet') },
};

// 使用環境變數或默認為 testnet
const defaultNetwork = process.env.REACT_APP_SUI_NETWORK || 'testnet';


// 語言列表
const supportedLangs = ['zh-TW', 'en', 'ja'];

function LangRouter() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const pathParts = location.pathname.split('/');
    const lang = pathParts[1];
    if (supportedLangs.includes(lang)) {
      if (i18n.language !== lang) i18n.changeLanguage(lang);
    } else {
      // 若網址沒語言，預設導向目前語言
      navigate(`/${i18n.language}${location.pathname}`, { replace: true });
    }
  }, [location.pathname, i18n, navigate]);

  return (
    <Routes>
      <Route path="/:lang" element={<Future250705 />} />
      {/* 其他多語頁面可在這裡加 */}
    </Routes>
  );
}

function App() {
  const hostname = window.location.hostname;
  const subdomain = hostname.split('.')[0];

  let routes;

  if (subdomain === 'stemcellsoft' || subdomain === 'www' || subdomain === 'localhost') {

    routes = (
      <Routes>
        <Route path="/" element={
          <Layout>
            <ProductList />
          </Layout>
        } />

        <Route path="/a" element={
          <Layout>
            <Account />
          </Layout>
        } />

        <Route path="/nomatch/*" element={
          <NoMatch />
        } />
        <Route path="/nomatch/chat/:roomId" element={
          <ProtectedRoute redirectTo="/nomatch" >
            <NoMatchChatRoom />
          </ProtectedRoute>
        } />
        <Route path="/nomatch/settings" element={
          <ProtectedRoute redirectTo="/nomatch">
            <NoMatchSettings />
          </ProtectedRoute>
        } />




        <Route path="/gl-sui" element={
          <ZkGoogleCallback />
        } />

        <Route path="/2048sui" element={<Game2048Sui />} />
        <Route path="/2048sui/*" element={<Game2048Sui />} />

        <Route path="/2048eth" element={<Game2048Eth />} />
        <Route path="/2048eth/*" element={<Game2048Eth />} />
      </Routes>
    );
  } else if (subdomain === 'aimeetingpro') {
    routes = (
      <Routes>
        <Route path="/" element={<AIMeetingPro />} />
        <Route path="/download" element={<AIMeetingProDownload />} />
        <Route path="/p" element={<AIMeetingProPost />} />
      </Routes>
    );
  } else if (subdomain === 'chatgptsaver') {
    routes = (
      <Routes>
        <Route path="/" element={<ChatGPTSaverPrivacy />} />
      </Routes>
    );
  } else if (subdomain === 'blog') {
    routes = (
      <Layout>
        <Routes>
          <Route path="/a" element={<Account />} />
          <Route path="/p" element={<Post />} />
          <Route path="/" element={<Post />} />
          <Route path="/m" element={<MarkdownTest />} />
          <Route path="/mi" element={
            <ProtectedRoute>
              <MarkdownTestInput />
            </ProtectedRoute>
          } />
          <Route path="/mu" element={
            <ProtectedRoute>
              <MarkdownUpdate />
            </ProtectedRoute>
          } />
          <Route path="/cp" element={
            <ProtectedRoute>
              <CreatePost />
            </ProtectedRoute>
          } />
          <Route path="/jlIf1" element={
            <AuthProvider>
              <Login />
            </AuthProvider>
          } />
        </Routes>
      </Layout>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <SuiClientProvider networks={networks} defaultNetwork={defaultNetwork}>
        <EnokiWalletRegistration />
        <WalletProvider autoConnect>
          <Router>{routes}</Router>
        </WalletProvider>
      </SuiClientProvider>
    </QueryClientProvider>
  );
}

export default App;